// src/Pages/Transactions/Transaction.js
import React, { useEffect, useRef, useState } from 'react'
import { useAlert } from 'react-alert'
import LoaderWrapper from '../../Components/LoaderWrapper/LoaderWrapper'
import ReactPaginate from 'react-paginate'
import { ReactComponent as Asc } from '../../assets/images/icons/ace.svg'
import { ReactComponent as Desc } from '../../assets/images/icons/desc.svg'
import _ from 'lodash'
import moment from 'moment'
import { Modal, ModalBody } from 'reactstrap'
import { transactionList } from '../../API/Services/transactionService'
import { getDateFromNo } from '../../helper/dateData'
import { CloseMiniIcon } from '../../Components/svg'
import './transaction.css' // same-folder CSS

// ───────────────────────── Helpers ─────────────────────────
const fmtMoney = (v) => {
  if (v === 0) return '0 NGN'
  if (v == null) return '-'
  const n = typeof v === 'string' ? parseFloat(v) : Number(v)
  if (Number.isNaN(n)) return '-'
  return `${n.toLocaleString()} NGN`
}

// Same value, but rendered with the Naira symbol for the **mobile cards only**
const fmtMoneyNaira = (v) => {
  if (v === 0) return '₦0'
  if (v == null) return '₦—'
  const n = typeof v === 'string' ? parseFloat(v) : Number(v)
  if (Number.isNaN(n)) return '₦—'
  return `₦${n.toLocaleString()}`
}

const fmtDate = (iso) => (iso ? moment(iso).format('DD/MM/YYYY') : '-')

const totalSchedule = (req) => {
  const bd = Array.isArray(req?.paymentBreakdown) ? req.paymentBreakdown : []
  if (!bd.length) return 0
  return bd.reduce((s, b) => {
    const n = typeof b.amount === 'string' ? parseFloat(b.amount) : Number(b.amount || 0)
    return s + (Number.isNaN(n) ? 0 : n)
  }, 0)
}

const installmentsSummary = (req) => {
  const bd = Array.isArray(req?.paymentBreakdown) ? req.paymentBreakdown : []
  if (!bd.length) return '-'
  const per = bd[0]?.amount
  const n = typeof per === 'string' ? parseFloat(per) : Number(per)
  const perText = Number.isNaN(n) ? per : n.toLocaleString()
  return `${bd.length} × ₦${perText}`
}

const nextDueOf = (req) => {
  if (req?.dueDate) return fmtDate(req.dueDate)

  const bd = Array.isArray(req?.paymentBreakdown) ? req.paymentBreakdown : []
  if (bd.length) {
    const now = Date.now()
    const future = bd
      .map(b => new Date(b.dueDate))
      .filter(d => !isNaN(d.getTime()) && d.getTime() >= now)
      .sort((a, b) => a.getTime() - b.getTime())
    if (future[0]) return moment(future[0]).format('DD/MM/YYYY')
    return moment(new Date(bd[bd.length - 1].dueDate)).format('DD/MM/YYYY')
  }

  if (req?.salaryDueDate) {
    return /^\d+$/.test(String(req.salaryDueDate))
      ? getDateFromNo(req.salaryDueDate)
      : req.salaryDueDate
  }
  return '-'
}

// ───────────────────── Mobile Card Component ─────────────────────
const MobileTxnCard = ({ request, index, limit, pageNumber, onOpen }) => {
  const amount = request?.amount ?? request?.requestedAmount
  const created = request?.createdAt || (request?.createdDate ? new Date(request.createdDate).toISOString() : null)

  return (
    <div
      className="txn-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(request)}
      onKeyDown={(e) => (e.key === 'Enter' ? onOpen(request) : null)}
      title="Tap to view details"
    >
      <div className="txn-card__top">
        <span className="txn-card__type">{request?.type || '—'}</span>
        <span className="txn-card__date">{fmtDate(created)}</span>
      </div>

      <div className="txn-card__amount">{fmtMoneyNaira(amount)}</div>

      <div className="txn-card__bottom">
        <span className={`status_badge status_${request?.status || 'na'}`}>{request?.status || '-'}</span>
        <span className="txn-card__sn">S/N {(index + 1) + ((pageNumber - 1) * limit)}</span>
      </div>

      <div className="txn-card__meta">
        <div><span className="meta-label">Request ID</span><span className="meta-val">{request?.generatedRequestId || '-'}</span></div>
        <div><span className="meta-label">Next/Due</span><span className="meta-val">{nextDueOf(request)}</span></div>
        <div><span className="meta-label">Interest</span><span className="meta-val">{fmtMoneyNaira(request?.interestAmount)}</span></div>
        <div><span className="meta-label">Installments</span><span className="meta-val">{installmentsSummary(request)}</span></div>
        <div className="col-span-2">
          <span className="meta-label">Total Payable</span>
          <span className="meta-val">
            {request.paymentBreakdown?.length
              ? fmtMoneyNaira(totalSchedule(request))
              : fmtMoneyNaira((Number(request.amount || 0) + Number(request.interestAmount || 0)))
            }
          </span>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────── Component ─────────────────────────
const Transaction = () => {
  const pagination = useRef()
  const alert = useAlert()
  const [loader, setLoader] = useState(false)
  const [apiData, setApiData] = useState({ requests: [], total: 0 })
  const [profileType, setProfiteType] = useState(1)
  const [pageNumber, setPageNumber] = useState(1)
  const [limit, setLimit] = useState(10)
  const [searchString, setSearchString] = useState('')
  const delayedQuery = useRef(_.debounce(q => sendQuery(q), 1000)).current
  const [searchQuery, setSearchQuery] = useState('')
  const [viewRequest, setViewRequest] = useState(null)
  const [sorting, setSorting] = useState({ order: -1, keyName: 'createdAt' })

  useEffect(() => {
    getAllTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileType, sorting, pageNumber, limit, searchQuery])

  const getAllTransactions = () => {
    setLoader(true)
    transactionList(profileType, sorting, pageNumber, limit, searchString)
      .then((response) => {
        if (response?.data?.status === 'OK') {
          const data = response?.data?.data || {}
          setApiData({ requests: data.requests || [], total: data.total || 0 })
        }
        setLoader(false)
      })
      .catch(error => {
        alert.error(error?.message || 'Something went wrong')
        setLoader(false)
      })
  }

  const setPage = ({ selected }) => setPageNumber(selected + 1)

  const toggleSort = (keyName) => {
    setSorting(prev => ({
      keyName,
      order: prev.keyName === keyName ? (prev.order === 1 ? -1 : 1) : 1
    }))
  }

  const handleSearch = (e) => {
    setSearchString(e.target.value)
    delayedQuery(e.target.value)
  }
  const sendQuery = (q) => setSearchQuery(q)

  const handleClose = () => setViewRequest(null)

  return (
    <>
      {/* Existing modal stays the same */}
      <Modal
        isOpen={!!viewRequest}
        toggle={handleClose}
        className='view_request_modal'
        centered
        backdrop
      >
        <ModalBody>
          {viewRequest && (
            <>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <p className='mb-0'>
                  <strong>REQUEST ID:</strong> {viewRequest.generatedRequestId || '-'}
                </p>
                <div className='close_mini_btn' onClick={handleClose}><CloseMiniIcon /> CLOSE</div>
              </div>

              <div className='row view_request_contain g-3'>
                <div className='col-md-4'>
                  <p>TYPE</p>
                  <h3 className='text-capitalize'>{viewRequest.type || '-'}</h3>
                </div>
                <div className='col-md-4'>
                  <p>STATUS</p>
                  <h3 className={`status_badge status_${viewRequest.status || 'na'}`}>{viewRequest.status || '-'}</h3>
                </div>

                <div className='col-md-4'>
                  <p>AMOUNT REQUESTED</p>
                  <h3>{fmtMoney(viewRequest.amount)}</h3>
                </div>
                <div className='col-md-4'>
                  <p>INTEREST</p>
                  <h3>{fmtMoney(viewRequest.interestAmount)}</h3>
                </div>
                <div className='col-md-4'>
                  <p>TOTAL PAYABLE</p>
                  <h3>{
                    viewRequest.paymentBreakdown?.length
                      ? fmtMoney(totalSchedule(viewRequest))
                      : fmtMoney((Number(viewRequest.amount || 0) + Number(viewRequest.interestAmount || 0)))
                  }</h3>
                </div>

                <div className='col-md-4'>
                  <p>CREATED</p>
                  <h3>{fmtDate(viewRequest.createdAt || (viewRequest.createdDate ? new Date(viewRequest.createdDate).toISOString() : null))}</h3>
                </div>
                <div className='col-md-4'>
                  <p>NEXT / DUE DATE</p>
                  <h3>{nextDueOf(viewRequest)}</h3>
                </div>
                <div className='col-md-4'>
                  <p>PAID?</p>
                  <h3>{viewRequest.isPaid ? 'Yes' : 'No'}</h3>
                </div>

                <div className='col-md-12'>
                  <p>ACCOUNT DETAILS</p>
                  <h3>
                    {viewRequest?.user?.bankDetails?.accountNo || '-'}{' '}
                    <small>({viewRequest?.user?.bankDetails?.bankName || '—'})</small>
                  </h3>
                </div>

                {Array.isArray(viewRequest.paymentBreakdown) && viewRequest.paymentBreakdown.length > 0 && (
                  <div className='col-12 mt-3'>
                    <div className='table-responsive'>
                      <table className='table table-sm table-borderless mb-0'>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Due Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewRequest.paymentBreakdown.map((b, i) => (
                            <tr key={b._id || i}>
                              <td>{i + 1}</td>
                              <td>{fmtDate(b.dueDate)}</td>
                              <td>{fmtMoney(b.amount)}</td>
                              <td className={`text-capitalize status_cell status_${b.paymentStatus}`}>{b.paymentStatus}</td>
                              <td>{b.invoiceRaised ? 'Yes' : 'No'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="d-flex justify-content-end mt-2">
                      <div className="text-muted small">
                        Installments: <strong>{installmentsSummary(viewRequest)}</strong> &nbsp;|&nbsp; Total:&nbsp;
                        <strong>{fmtMoney(totalSchedule(viewRequest))}</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <div className="generic-wrapper">
        <h3 className="generic-heading">All Requests</h3>

        {/* Desktop table view (unchanged) */}
        <div className="generic-table">
          <div className="data-table table-responsive table-view">
            <table className="table table-borderless">
              <thead>
                <tr>
                  <th scope="col">S/N</th>
                  <th scope="col">TYPE</th>
                  <th scope="col">REQUEST ID</th>
                  <th scope="col">AMOUNT</th>
                  <th scope="col">INTEREST</th>
                  <th scope="col">
                    CREATED
                    {(sorting.keyName === 'createdAt' && sorting.order === 1)
                      ? <Desc className="ascending_icons ms-2" onClick={() => toggleSort('createdAt')} />
                      : <Asc className="ascending_icons ms-2" onClick={() => toggleSort('createdAt')} />
                    }
                  </th>
                  <th scope="col">
                    NEXT / DUE DATE
                    {(sorting.keyName === 'dueDate' && sorting.order === 1)
                      ? <Desc className="ascending_icons ms-2" onClick={() => toggleSort('dueDate')} />
                      : <Asc className="ascending_icons ms-2" onClick={() => toggleSort('dueDate')} />
                    }
                  </th>
                  <th scope="col">INSTALLMENTS</th>
                  <th scope="col">STATUS</th>
                  {/* <th scope="col">MORE</th> */}
                </tr>
              </thead>
              <tbody>
                {apiData?.requests?.map((request, index) => {
                  const amount = request?.amount ?? request?.requestedAmount
                  return (
                    <tr
                      key={request?.id || index}
                      className="row_click"
                      onClick={() => setViewRequest(request)}
                      title="Click to view details"
                    >
                      <td className="pt-md-4 pt-3">{((index + 1) + ((pageNumber - 1) * limit))}</td>
                      <td className="pt-md-4 pt-3 text-capitalize">{request?.type || '-'}</td>
                      <td className="pt-md-4 pt-3">{request?.generatedRequestId || '-'}</td>
                      <td className="pt-md-4 pt-3">{fmtMoney(amount)}</td>
                      <td className="pt-md-4 pt-3">{fmtMoney(request?.interestAmount)}</td>
                      <td className="pt-md-4 pt-3">
                        {fmtDate(request?.createdAt || (request?.createdDate ? new Date(request.createdDate).toISOString() : null))}
                      </td>
                      <td className="pt-md-4 pt-3">{nextDueOf(request)}</td>
                      <td className="pt-md-4 pt-3">{installmentsSummary(request)}</td>
                      <td className="pt-md-4 pt-3 text-capitalize">
                        <span className={`status_badge status_${request?.status || 'na'}`}>
                          {request?.status || '-'}
                        </span>
                      </td>
                      {/* <td className="pt-md-4 pt-3">
                        <span onClick={(e) => { e.stopPropagation(); setViewRequest(request); }} className="btn-outline-incash link-primary">View</span>
                      </td> */}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list (only visible under 768px via CSS) */}
          <div className="mobile-list">
            {apiData?.requests?.map((request, index) => (
              <MobileTxnCard
                key={request?.id || request?.generatedRequestId || index}
                request={request}
                index={index}
                limit={limit}
                pageNumber={pageNumber}
                onOpen={(r) => setViewRequest(r)}
              />
            ))}
          </div>

          <div className="my-5 d-flex justify-content-center">
            <ReactPaginate
              ref={pagination}
              pageCount={Math.max(1, Math.ceil((apiData?.total || 0) / limit))}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={setPage}
              containerClassName="pagination"
              activeClassName="active"
              pageLinkClassName="page-link"
              breakLinkClassName="page-link"
              nextLinkClassName="page-link"
              previousLinkClassName="page-link"
              pageClassName="page-item"
              breakClassName="page-item"
              nextClassName="page-item"
              previousClassName="page-item"
              previousLabel={<>&laquo;</>}
              nextLabel={<>&raquo;</>}
            />
          </div>
        </div>
      </div>

      {loader ? <LoaderWrapper /> : null}
    </>
  )
}

export default Transaction
